import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';
import MUIProvider from '~/contexts/MUIProvider';
import { nativeLensId } from '~/nativeLenses/nativeLensId';

import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PhoneForwardedOutlinedIcon from '@mui/icons-material/PhoneForwardedOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import {
  Alert,
  Box,
  Button,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

import PageChatButton from '~/pages/AskRichardUnified/page/PageChatButton';
import PageChatLayout from '~/pages/AskRichardUnified/page/PageChatLayout';
import { usePageBridge } from '~/pages/AskRichardUnified/page/pageBridge';

import { useGetReportingLeLensData } from '~/hooks/apis/reporting/useGetReportingLeLensData';

import type ApiError from '~/types/ApiError';
import type { LensCardData } from '~/types/leLens';

import {
  CATALOGUE,
  COMPARISONS,
  CS_DAILY_OPS_PAGE_ID,
  type Filters,
  type Item,
  type LayoutCard,
  PRESETS,
  SECTIONS,
  SELECTS,
  TABS,
  bucketMedian,
  delta,
  deltaGood,
  deltaText,
  formatValue,
  lensRequest,
  num,
  parseFilters,
  sentence,
  text,
  writeFilters,
} from './model';
import { csDailyOpsPageView, editFromBridge } from './pageAdapter';

const LENS_ID = nativeLensId(CS_DAILY_OPS_PAGE_ID);
const SOURCE =
  'reporting.cs_daily_metrics, cs_daily_team_metrics, contact_centre_contacts, contact_centre_agent_activity, zendesk_open_book_daily and fact_zendesk_ticket, built daily at 03:00 Sydney. Live board: reporting.genesys_agent_calls and the stage Zendesk tables, landed hourly.';
const SERIES = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#8e5bd6'];
/** Stacked segments: New work not yet with an agent is a different kind of category, so it is neutral grey. */
const segmentColour = (key: string, i: number) => (key === 'New' ? '#9a9a94' : SERIES[i % SERIES.length]);
const ICONS: Record<string, React.ReactNode> = {
  phone: <PhoneOutlinedIcon fontSize="small" />,
  'phone-outgoing': <PhoneForwardedOutlinedIcon fontSize="small" />,
  'message-circle': <ChatBubbleOutlineIcon fontSize="small" />,
  mail: <MailOutlineIcon fontSize="small" />,
};
const NUM = { fontVariantNumeric: 'tabular-nums' } as const;

/* ---------- reading the engine's cards ---------- */
type Reader = {
  card: (id: string) => LensCardData | undefined;
  problem: (id: string) => string | null;
  rows: (id: string) => Array<Record<string, unknown>>;
  filtersActive: boolean;
  periodDays: number;
  period: { from: string; to: string } | null;
};

/** Weekdays and weekend days (Sat, Sun) between two ISO dates, inclusive. */
const dayTypeCounts = (period: { from: string; to: string } | null) => {
  const out = { Weekday: 0, Weekend: 0 };
  if (!period) {
    return out;
  }
  for (
    let d = new Date(`${period.from}T00:00:00Z`);
    d <= new Date(`${period.to}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + 1)
  ) {
    out[d.getUTCDay() === 0 || d.getUTCDay() === 6 ? 'Weekend' : 'Weekday'] += 1;
  }
  return out;
};
type Figure = {
  item: Item;
  current: number | null;
  previous: number | null;
  problem: string | null;
  alt: boolean;
  lag?: string;
};

const readRatio = (row: Record<string, unknown> | undefined, of: string, per: string) => {
  if (!row) {
    return null;
  }
  const top = num(row[of]);
  const base = num(row[per]);
  if (top !== null && base !== null) {
    return base === 0 ? null : top / base;
  }
  const direct = num(row.ratio ?? row.value);
  if (direct !== null) {
    return direct;
  }
  const numbers = Object.values(row)
    .map(num)
    .filter((value): value is number => value !== null);
  return numbers.length >= 2 && numbers[1] !== 0 ? numbers[0] / numbers[1] : null;
};

const shift = (day: string, days: number) => {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

function readItem(id: string, reader: Reader): Figure | null {
  const alt = reader.filtersActive && CATALOGUE.itemIndex[`${id}_team`] ? `${id}_team` : id;
  const index = CATALOGUE.itemIndex[alt];
  if (!index) {
    return null;
  }
  const card = reader.card(index.cardId);
  const row = card?.rows?.[0];
  const prev = card?.comparison?.rows?.[0];
  const read = (r: Record<string, unknown> | undefined) =>
    index.buckets
      ? bucketMedian(r, index.buckets)
      : index.ratio
        ? readRatio(r, index.ratio.of, index.ratio.per)
        : num(r?.[index.column ?? '']);
  const current = read(row);
  const previous = read(prev);
  const item: Item = {
    ...index,
    label: index.label,
    format: index.format,
    meaning: index.meaning,
  };
  const lag =
    index.lagDays && card?.period
      ? `for ${shift(card.period.current.from, -index.lagDays)} to ${shift(card.period.current.to, -index.lagDays)}`
      : undefined;
  return { item, current, previous, problem: reader.problem(index.cardId), alt: alt !== id, lag };
}

/* ---------- small pieces, as on the mockup ---------- */
function DeltaLine({ f, cmpLabel }: { f: Figure; cmpLabel: string }) {
  const d = delta(f.current, f.previous, f.item.format);
  if (!d) {
    return null;
  }
  const good = deltaGood(d, f.item.meaning);
  return (
    <Typography variant="caption" component="div" color="text.secondary" sx={{ lineHeight: 1.3 }}>
      <Box component="b" sx={{ color: good === null ? 'text.primary' : good ? 'success.main' : 'error.main' }}>
        {deltaText(d)}
      </Box>{' '}
      {cmpLabel}
    </Typography>
  );
}

function Value({ f, size, cmpLabel }: { f: Figure; size: 'hero' | 'box' | 'stat'; cmpLabel: string }) {
  const fontSize = size === 'hero' ? 30 : size === 'box' ? 18 : 22;
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        component="div"
        sx={{ fontSize: size === 'box' ? 11 : 12, lineHeight: 1.3 }}
      >
        {f.item.label}
        <Info text={f.lag} />
      </Typography>
      <Typography component="div" sx={{ fontSize, fontWeight: 600, lineHeight: 1.1, letterSpacing: '-0.01em', ...NUM }}>
        {formatValue(f.current, f.item.format)}
      </Typography>
      <DeltaLine f={f} cmpLabel={cmpLabel} />
    </Box>
  );
}

function Meter({ f, label, cmpLabel }: { f: Figure; label: string; cmpLabel: string }) {
  const value = f.current;
  const pct = value === null ? 0 : Math.max(0, Math.min(100, value * 100));
  const target = f.item.target;
  const met = target === undefined || value === null ? null : value >= target;
  const gap = target === undefined || value === null ? null : (value - target) * 100;
  const d = delta(value, f.previous, f.item.format);
  const good = deltaGood(d, f.item.meaning);
  // green / red only judge a target; with no target the bar is a neutral data colour
  const tone = met === null ? SERIES[0] : met ? 'success.main' : 'error.main';
  // keep the target label inside the bar's width near either end
  const shiftLabel =
    target === undefined ? '' : target > 0.85 ? 'translateX(-100%)' : target < 0.15 ? 'none' : 'translateX(-50%)';
  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
          {label}
        </Typography>
        <Tooltip title={formatValue(value, 'percent')} arrow>
          <Typography
            sx={{ ...NUM, fontSize: 20, fontWeight: 700, lineHeight: 1.1, color: met === null ? 'text.primary' : tone }}
          >
            {value === null ? '—' : `${Math.round(value * 100)}%`}
          </Typography>
        </Tooltip>
      </Stack>
      <Box sx={{ position: 'relative', mt: 0.75, mb: target === undefined ? 0.75 : 2.5 }}>
        <Box sx={{ height: 10, borderRadius: 5, bgcolor: 'action.hover', overflow: 'hidden' }}>
          <Box sx={{ width: `${pct}%`, height: 10, borderRadius: 5, bgcolor: tone }} />
        </Box>
        {target !== undefined && (
          <>
            <Box
              sx={{
                position: 'absolute',
                top: -3,
                left: `${target * 100}%`,
                width: 2,
                height: 16,
                bgcolor: 'text.primary',
                transform: 'translateX(-1px)',
              }}
            />
            <Typography
              component="div"
              sx={{
                position: 'absolute',
                top: 15,
                left: `${target * 100}%`,
                transform: shiftLabel,
                fontSize: 11,
                color: 'text.secondary',
                whiteSpace: 'nowrap',
              }}
            >
              Target {formatValue(target, 'percent').replace('.0%', '%')}
            </Typography>
          </>
        )}
      </Box>
      <Typography component="div" sx={{ fontSize: 12, lineHeight: 1.5, color: 'text.secondary' }}>
        {gap !== null && (
          <Box component="span" sx={{ display: 'block', color: tone, fontWeight: 600 }}>
            {Math.abs(gap) < 0.05
              ? 'On target'
              : `${Math.abs(gap).toFixed(1)} pts ${gap > 0 ? 'above' : 'below'} target`}
          </Box>
        )}
        {d && (
          <Box component="span" sx={{ display: 'block' }}>
            <Box
              component="span"
              sx={{ fontWeight: 600, color: good === null ? 'text.primary' : good ? 'success.main' : 'error.main' }}
            >
              {d.value === 0 ? '■' : d.value > 0 ? '▲' : '▼'} {Math.abs(d.value).toFixed(1)}{' '}
              {d.kind === 'pts' ? 'pts' : '%'}
            </Box>{' '}
            {cmpLabel}
          </Box>
        )}
      </Typography>
    </Box>
  );
}

/** A small "i" that explains a reading window on hover. */
function Info({ text: note }: { text?: string }) {
  if (!note) {
    return null;
  }
  return (
    <Tooltip title={note} arrow>
      <InfoOutlinedIcon
        sx={{ fontSize: 15, ml: 0.5, verticalAlign: 'text-bottom', color: 'text.disabled', cursor: 'help' }}
      />
    </Tooltip>
  );
}

function Card({ title, lag, children }: { title: string; lag?: string; children: React.ReactNode }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, height: '100%' }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        {title}
        <Info text={lag} />
      </Typography>
      {children}
    </Paper>
  );
}

function Problem({ text: message }: { text: string | null }) {
  return message ? (
    <Typography variant="caption" color="text.secondary" component="div">
      {message}
    </Typography>
  ) : null;
}

/* ---------- cards ---------- */
function ChannelsCard({ layout, reader, cmpLabel }: { layout: LayoutCard; reader: Reader; cmpLabel: string }) {
  return (
    <Card title={layout.title}>
      {(layout.rows ?? []).map((row, i) => {
        const hero = readItem(row.hero, reader);
        return (
          <Box
            key={row.id}
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'minmax(200px, 0.8fr) minmax(280px, 1.2fr) 240px' },
              gap: 2,
              py: 1.75,
              borderTop: i ? '1px solid' : 0,
              borderColor: 'divider',
              alignItems: 'center',
            }}
          >
            <Stack direction="row" spacing={1.25} alignItems="flex-start">
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  bgcolor: 'action.hover',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: 'none',
                }}
              >
                {ICONS[row.icon]}
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 600 }}>{row.name}</Typography>
                {hero && (
                  <Value
                    f={{ ...hero, item: { ...hero.item, label: row.heroLabel } }}
                    size="hero"
                    cmpLabel={cmpLabel}
                  />
                )}
              </Box>
            </Stack>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
              {row.boxes.map((id) => {
                const f = readItem(id, reader);
                return (
                  <Box key={id} sx={{ bgcolor: 'action.hover', borderRadius: 2.5, px: 1.25, py: 1 }}>
                    {f ? <Value f={f} size="box" cmpLabel={cmpLabel} /> : <Typography variant="caption">—</Typography>}
                  </Box>
                );
              })}
            </Box>
            <Box>
              {(row.meters ?? []).map((m) => {
                const f = readItem(m.item, reader);
                return f ? <Meter key={m.item} f={f} label={m.label} cmpLabel={cmpLabel} /> : null;
              })}
            </Box>
          </Box>
        );
      })}
    </Card>
  );
}

function StatsCard({ layout, reader, cmpLabel }: { layout: LayoutCard; reader: Reader; cmpLabel: string }) {
  return (
    <Card title={layout.title}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px 14px' }}>
        {(layout.items ?? []).map((it) => {
          if (it.na) {
            return null;
          }
          const f = readItem(it.id, reader);
          return f ? <Value key={it.id} f={f} size="stat" cmpLabel={cmpLabel} /> : null;
        })}
      </Box>
    </Card>
  );
}

function MatrixCard({ layout, reader, cmpLabel }: { layout: LayoutCard; reader: Reader; cmpLabel: string }) {
  return (
    <Card title={layout.title}>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell />
              {(layout.columns ?? []).map((c) => (
                <TableCell key={c.id} align="right">
                  {c.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {(layout.rows ?? []).map((row) => (
              <TableRow key={row.label}>
                <TableCell>
                  {row.label}
                  {row.info && (
                    <Info
                      text={
                        row.info === 'fcr'
                          ? Object.values(row.cells ?? {})
                              .map((cell) => (typeof cell === 'string' ? readItem(cell, reader)?.lag : undefined))
                              .find(Boolean)
                          : row.info
                      }
                    />
                  )}
                </TableCell>
                {(layout.columns ?? []).map((c) => {
                  const cell = row.cells?.[c.id];
                  if (!cell) {
                    return (
                      <TableCell key={c.id} align="right" sx={{ color: 'text.disabled' }}>
                        —
                      </TableCell>
                    );
                  }
                  if (typeof cell === 'object') {
                    return (
                      <TableCell key={c.id} align="right" sx={{ color: 'text.disabled', fontSize: 12 }}>
                        {cell.text}
                      </TableCell>
                    );
                  }
                  const f = readItem(cell, reader);
                  if (!f) {
                    return (
                      <TableCell key={c.id} align="right">
                        —
                      </TableCell>
                    );
                  }
                  return (
                    <TableCell key={c.id} align="right" sx={{ verticalAlign: 'top' }}>
                      <Box component="span" sx={{ fontWeight: 600, fontSize: 15, ...NUM }}>
                        {formatValue(f.current, f.item.format)}
                      </Box>
                      <DeltaLine f={f} cmpLabel={cmpLabel} />
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
}

const sumBy = (
  rows: Array<Record<string, unknown>>,
  key: string,
  measures: Array<string>,
  filter?: (r: Record<string, unknown>) => boolean,
) => {
  const out = new Map<string, Record<string, number>>();
  rows.forEach((r) => {
    if (filter && !filter(r)) {
      return;
    }
    const k = text(r[key]);
    if (!out.has(k)) {
      out.set(k, Object.fromEntries(measures.map((m) => [m, 0])));
    }
    const o = out.get(k);
    measures.forEach((m) => {
      o[m] += num(r[m]) ?? 0;
    });
  });
  return out;
};

function StackedCard({ layout, reader }: { layout: LayoutCard; reader: Reader }) {
  const v = layout.view;
  const rows = reader.rows(layout.lensCardId);
  const g = sumBy(rows, v.x, [v.value]);
  const keys = (v.order ?? [...g.keys()]).map(String).filter((k) => g.has(k));
  const total = keys.reduce((s, k) => s + g.get(k)[v.value], 0);
  const label = (k: string) => (v.labels && !Array.isArray(v.labels) ? v.labels[k] : undefined) ?? sentence(k);
  return (
    <Card title={layout.title}>
      <Problem text={reader.problem(layout.lensCardId)} />
      <Typography sx={{ fontSize: 30, fontWeight: 600, lineHeight: 1.1, ...NUM }}>{formatValue(total)}</Typography>
      <Box sx={{ display: 'flex', height: 22, gap: '2px', mt: 1, borderRadius: 1.5, overflow: 'hidden' }}>
        {keys.map((k, i) => {
          const n = g.get(k)[v.value];
          const pct = total ? (n / total) * 100 : 0;
          return (
            <Tooltip key={k} title={`${label(k)}: ${formatValue(n)} (${pct.toFixed(1)}%)`} arrow>
              <Box
                sx={{
                  width: `${pct}%`,
                  bgcolor: segmentColour(k, i),
                  color: '#fff',
                  fontSize: 11,
                  lineHeight: '22px',
                  textAlign: 'center',
                  whiteSpace: 'nowrap',
                }}
              >
                {pct >= 12 ? `${pct.toFixed(0)}%` : ''}
              </Box>
            </Tooltip>
          );
        })}
      </Box>
      <Stack direction="row" flexWrap="wrap" useFlexGap sx={{ gap: '6px 14px', mt: 1 }}>
        {keys.map((k, i) => (
          <Typography key={k} variant="caption" color="text.secondary">
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                width: 10,
                height: 10,
                borderRadius: 0.5,
                bgcolor: segmentColour(k, i),
                mr: 0.5,
                verticalAlign: -1,
              }}
            />
            {label(k)} {formatValue(g.get(k)[v.value])} · {total ? ((g.get(k)[v.value] / total) * 100).toFixed(0) : 0}%
          </Typography>
        ))}
      </Stack>
    </Card>
  );
}

function BarCard({ layout, reader, lagLabel }: { layout: LayoutCard; reader: Reader; lagLabel?: string }) {
  const v = layout.view;
  const rows = reader.rows(layout.lensCardId);
  const g = sumBy(rows, v.x, [v.value]);
  // ordinal categories (age bands, scores) keep their order; everything else runs highest to lowest
  const keys = v.order
    ? v.order.map(String).filter((k) => g.has(k))
    : [...g.keys()].sort((a, b) => g.get(b)[v.value] - g.get(a)[v.value] || a.localeCompare(b));
  const total = keys.reduce((s, k) => s + g.get(k)[v.value], 0);
  const max = Math.max(1, ...keys.map((k) => g.get(k)[v.value]));
  const label = (k: string) => (v.labels && !Array.isArray(v.labels) ? v.labels[k] : undefined) ?? sentence(k);
  return (
    <Card title={layout.title} lag={lagLabel}>
      <Problem text={reader.problem(layout.lensCardId)} />
      <Stack spacing={1}>
        {keys.map((k) => {
          const n = g.get(k)[v.value];
          return (
            <Tooltip
              key={k}
              title={`${label(k)}: ${formatValue(n)}${total ? ` · ${((n / total) * 100).toFixed(1)}%` : ''}`}
              arrow
              placement="left"
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(80px, 160px) 1fr auto',
                  gap: 1,
                  alignItems: 'center',
                  fontSize: 13,
                }}
              >
                <Typography variant="body2" color="text.secondary" noWrap>
                  {label(k)}
                </Typography>
                <Box sx={{ height: 14 }}>
                  <Box
                    sx={{
                      height: 14,
                      width: `${(n / max) * 100}%`,
                      bgcolor: SERIES[0],
                      borderRadius: '0 4px 4px 0',
                      minWidth: 2,
                    }}
                  />
                </Box>
                <Typography variant="body2" sx={{ ...NUM, whiteSpace: 'nowrap' }}>
                  {formatValue(n)}
                  {v.share && total ? ` · ${((n / total) * 100).toFixed(0)}%` : ''}
                </Typography>
              </Box>
            </Tooltip>
          );
        })}
        {!keys.length && (
          <Typography variant="caption" color="text.disabled">
            No rows in this slice.
          </Typography>
        )}
      </Stack>
    </Card>
  );
}

function TableCard({ layout, reader, lagLabel }: { layout: LayoutCard; reader: Reader; lagLabel?: string }) {
  const v = layout.view;
  // a second grouping key (x2) folds into one row label, e.g. "ANZ · Weekday"
  const rows = v.x2
    ? reader.rows(layout.lensCardId).map((r) => ({ ...r, __x: `${text(r[v.x])} · ${text(r[v.x2])}` }))
    : reader.rows(layout.lensCardId);
  const xKey = v.x2 ? '__x' : v.x;
  const days = dayTypeCounts(reader.period);
  const divisor = (x: string) =>
    v.x2 === 'day_type' && x !== '__total'
      ? Math.max(1, x.endsWith('Weekend') ? days.Weekend : days.Weekday)
      : Math.max(1, reader.periodDays);
  const problem = reader.problem(layout.lensCardId);
  const measures = v.values ?? [];
  const pivots = v.pivots ?? {};
  const derived = v.derived ?? {};
  const guards = v.guards ?? {};
  let header: Array<string>;
  let body: Array<{ label: string; cells: Array<string> }>;
  let totalRow: Array<string> | null = null;
  if (v.series && !measures.length) {
    // pivot: x by series for one measure, as the mockup's priority-by-team table
    const cols = v.seriesOrder ?? [...new Set(rows.map((r) => text(r[v.series])))].sort();
    const byX = new Map<string, Map<string, number>>();
    rows.forEach((r) => {
      const x = text(r[v.x]);
      if (!byX.has(x)) {
        byX.set(x, new Map());
      }
      const m = byX.get(x);
      m.set(text(r[v.series]), (m.get(text(r[v.series])) ?? 0) + (num(r[v.value]) ?? 0));
    });
    header = [...cols.map(sentence), 'Total'];
    const totals = Object.fromEntries(cols.map((c) => [c, 0]));
    const rowTotal = (x: string) => [...byX.get(x).values()].reduce((a, b) => a + b, 0);
    body = [...byX.keys()]
      .sort((a, b) => rowTotal(b) - rowTotal(a) || a.localeCompare(b))
      .map((x) => {
        const m = byX.get(x);
        let t = 0;
        const cells = cols.map((c) => {
          const n = m.get(c) ?? 0;
          totals[c] += n;
          t += n;
          return formatValue(n);
        });
        return { label: x, cells: [...cells, formatValue(t)] };
      });
    totalRow = [
      ...cols.map((c) => formatValue(totals[c])),
      formatValue(Object.values(totals).reduce((a, b) => a + b, 0)),
    ];
  } else {
    // sums by x; pivot columns pick one series value; derived ratios; daily averages divide by the days in the window
    const all = [
      ...new Set([
        ...measures,
        ...Object.values(derived).flatMap((d) => [d.ratio.of, d.ratio.per]),
        ...Object.values(guards),
      ]),
    ].filter((m) => !(m in pivots));
    const g = sumBy(rows, xKey, all);
    const pv = new Map<string, Record<string, number>>();
    Object.entries(pivots).forEach(([pid, rule]) => {
      sumBy(rows, xKey, [rule.value], (r) => text(r[rule.column]) === rule.equals).forEach((o, k) => {
        if (!pv.has(k)) {
          pv.set(k, {});
        }
        pv.get(k)[pid] = o[rule.value];
      });
    });
    const shown = (v.values ?? []).concat(Object.keys(derived).filter((d) => !(v.values ?? []).includes(d)));
    const labels = Array.isArray(v.labels) ? v.labels : [];
    header = shown.map((m, i) => labels[i] ?? sentence(m));
    const cell = (x: string, m: string, o: Record<string, number>) => {
      const guard = guards[m];
      if (guard && !((o[guard] ?? 0) > 0)) {
        return '—';
      }
      if (derived[m]) {
        const top = o[derived[m].ratio.of] ?? 0;
        const base = o[derived[m].ratio.per] ?? 0;
        return formatValue(base ? top / base : null, derived[m].format);
      }
      if (m in pivots) {
        return formatValue(pv.get(x)?.[m] ?? 0);
      }
      const f = v.formats?.[m];
      return formatValue(
        v.aggregate === 'avg' ? (o[m] ?? 0) / divisor(x) : o[m],
        f ?? (v.aggregate === 'avg' ? 'number1' : undefined),
      );
    };
    // rows run highest to lowest on the first measure; a second key (day type) stays grouped under its first
    const first = measures.find((m) => !(m in pivots));
    const size = (x: string) => (first ? (g.get(x)[first] ?? 0) : 0);
    const groupKey = (x: string) => (v.x2 ? text(rows.find((r) => r.__x === x)?.[v.x]) : x);
    const groupSize = new Map<string, number>();
    [...g.keys()].forEach((x) => groupSize.set(groupKey(x), (groupSize.get(groupKey(x)) ?? 0) + size(x)));
    const xs = [...g.keys()].sort(
      (a, b) =>
        groupSize.get(groupKey(b)) - groupSize.get(groupKey(a)) ||
        groupKey(a).localeCompare(groupKey(b)) ||
        (v.x2 ? a.localeCompare(b) : size(b) - size(a)) ||
        a.localeCompare(b),
    );
    body = xs.map((x) => ({ label: x, cells: shown.map((m) => cell(x, m, g.get(x))) }));
    if (xs.length > 1) {
      const tot: Record<string, number> = {};
      all.forEach((m) => {
        tot[m] = xs.reduce((s, x) => s + (g.get(x)[m] ?? 0), 0);
      });
      pv.forEach((o) =>
        Object.entries(o).forEach(([k, n]) => {
          tot[k] = (tot[k] ?? 0) + n;
        }),
      );
      totalRow = shown.map((m) =>
        (v.noTotal ?? []).includes(m) ? '—' : m in pivots ? formatValue(tot[m] ?? 0) : cell('__total', m, tot),
      );
    }
  }
  return (
    <Card title={layout.title} lag={lagLabel}>
      <Problem text={problem} />
      <TableContainer sx={{ maxHeight: 440 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>{v.x2 ? `${sentence(v.x)} · ${sentence(v.x2)}` : sentence(v.x)}</TableCell>
              {header.map((h) => (
                <TableCell key={h} align="right">
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {body.map((r) => (
              <TableRow key={r.label}>
                <TableCell>{sentence(r.label)}</TableCell>
                {r.cells.map((c, i) => (
                  <TableCell key={i} align="right" sx={NUM}>
                    {c}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {totalRow && (
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                {totalRow.map((c, i) => (
                  <TableCell key={i} align="right" sx={{ fontWeight: 600, ...NUM }}>
                    {c}
                  </TableCell>
                ))}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {!body.length && !problem && (
        <Typography variant="caption" color="text.disabled">
          No rows in this slice.
        </Typography>
      )}
    </Card>
  );
}

function BarsCard({ layout, reader }: { layout: LayoutCard; reader: Reader }) {
  // created / solved by hour (one day) or by day (longer), with a hover tooltip per bucket
  const v = layout.view;
  const rows = reader.rows(layout.lensCardId);
  const series = v.values ?? [];
  const unit = reader.periodDays <= 1 ? 13 : 10;
  const buckets = new Map<string, Record<string, number>>();
  rows.forEach((r) => {
    const k = text(r.hour_sydt).slice(0, unit);
    if (!buckets.has(k)) {
      buckets.set(k, Object.fromEntries(series.map((s) => [s, 0])));
    }
    series.forEach((s) => {
      buckets.get(k)[s] += num(r[s]) ?? 0;
    });
  });
  const keys = [...buckets.keys()].sort();
  const [hover, setHover] = useState<number | null>(null);
  const W = 900;
  const H = 240;
  const pad = { l: 44, r: 16, t: 12, b: 28 };
  const iw = W - pad.l - pad.r;
  const ih = H - pad.t - pad.b;
  const max = Math.max(1, ...keys.flatMap((k) => series.map((s) => buckets.get(k)[s])));
  const nice = (() => {
    const p = Math.pow(10, Math.floor(Math.log10(max)));
    const r = max / p;
    return (r <= 1 ? 1 : r <= 2 ? 2 : r <= 2.5 ? 2.5 : r <= 5 ? 5 : 10) * p;
  })();
  const slot = iw / Math.max(1, keys.length);
  const gap = 2;
  const bw = Math.min(24, (slot - 6) / Math.max(1, series.length) - gap);
  const y = (n: number) => pad.t + ih - (n / nice) * ih;
  const step = Math.max(1, Math.ceil(keys.length / 8));
  const bucketLabel = (k: string) => (unit === 13 ? `${k.slice(0, 10)} ${k.slice(11)}:00` : k);
  return (
    <Card title={layout.title}>
      <Problem text={reader.problem(layout.lensCardId)} />
      <Box sx={{ position: 'relative' }} onMouseLeave={() => setHover(null)}>
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={layout.title}>
          {[0, 1, 2, 3, 4].map((i) => (
            <g key={i}>
              <line
                x1={pad.l}
                x2={pad.l + iw}
                y1={y((nice * i) / 4)}
                y2={y((nice * i) / 4)}
                stroke="currentColor"
                strokeOpacity={0.12}
              />
              <text
                x={pad.l - 6}
                y={y((nice * i) / 4) + 4}
                textAnchor="end"
                fontSize={11}
                fill="currentColor"
                fillOpacity={0.6}
              >
                {formatValue((nice * i) / 4)}
              </text>
            </g>
          ))}
          {keys.map((k, i) => {
            const x0 = pad.l + i * slot + (slot - (bw + gap) * series.length) / 2;
            return (
              <g key={k} onMouseEnter={() => setHover(i)}>
                <rect
                  x={pad.l + i * slot}
                  y={pad.t}
                  width={slot}
                  height={ih}
                  fill={hover === i ? 'currentColor' : 'transparent'}
                  fillOpacity={0.05}
                />
                {series.map((s, si) => (
                  <rect
                    key={s}
                    x={x0 + si * (bw + gap)}
                    y={y(buckets.get(k)[s])}
                    width={Math.max(1, bw)}
                    height={Math.max(0, (ih * buckets.get(k)[s]) / nice)}
                    fill={SERIES[si]}
                    rx={3}
                  />
                ))}
                {(i === keys.length - 1 || i % step === 0) && (
                  <text
                    x={pad.l + i * slot + slot / 2}
                    y={H - 8}
                    textAnchor="middle"
                    fontSize={11}
                    fill="currentColor"
                    fillOpacity={0.6}
                  >
                    {unit === 13 ? `${k.slice(11)}:00` : k.slice(5)}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
        {hover !== null && keys[hover] && (
          <Paper
            elevation={4}
            sx={{
              position: 'absolute',
              top: 8,
              left: `${Math.min(85, ((pad.l + hover * slot) / W) * 100)}%`,
              px: 1.25,
              py: 0.75,
              fontSize: 12,
              pointerEvents: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            <Typography variant="caption" color="text.secondary" component="div">
              {bucketLabel(keys[hover])}
            </Typography>
            {series.map((s, si) => (
              <Box key={s} component="div">
                <Box
                  component="span"
                  sx={{
                    display: 'inline-block',
                    width: 10,
                    height: 10,
                    borderRadius: 0.5,
                    bgcolor: SERIES[si],
                    mr: 0.5,
                    verticalAlign: -1,
                  }}
                />
                <b>{formatValue(buckets.get(keys[hover])[s])}</b> {v.labels?.[si] ?? sentence(s)}
              </Box>
            ))}
          </Paper>
        )}
      </Box>
      <Stack direction="row" spacing={2}>
        {series.map((s, si) => (
          <Typography key={s} variant="caption" color="text.secondary">
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                width: 10,
                height: 10,
                borderRadius: 0.5,
                bgcolor: SERIES[si],
                mr: 0.5,
                verticalAlign: -1,
              }}
            />
            {v.labels?.[si] ?? sentence(s)}
          </Typography>
        ))}
      </Stack>
    </Card>
  );
}

/* ---------- agents tab ---------- */
type AgentColumn = (typeof CATALOGUE.agentsTab.columns)[number];
type AgentNode = {
  key: string;
  name: string;
  team: string;
  leader: string;
  row: Record<string, number>;
  f: Record<string, number>;
  agents: number;
  items: number;
};
const AGENT_META = ['agent_key', 'agent_name', 'team', 'team_leader', 'region', 'department', 'activity_date_syd'];
const CONTACT_COLUMNS = ['calls_answered', 'callbacks_answered', 'chats_handled', 'emails_handled'];

function sumNodes(key: string, name: string, team: string, leader: string, parts: Array<AgentNode>): AgentNode {
  const row: Record<string, number> = {};
  const f: Record<string, number> = {};
  parts.forEach((p) => {
    Object.entries(p.row).forEach(([k, v]) => {
      row[k] = (row[k] ?? 0) + v;
    });
    Object.entries(p.f).forEach(([k, v]) => {
      f[k] = (f[k] ?? 0) + v;
    });
  });
  return {
    key,
    name,
    team,
    leader,
    row,
    f,
    agents: parts.reduce((n, p) => n + p.agents, 0),
    items: parts.reduce((n, p) => n + p.items, 0),
  };
}

function agentValue(node: AgentNode, column: AgentColumn): number | null {
  const source = column.kind === 'fcr' ? node.f : node.row;
  if (column.kind === 'sum' || !column.per) {
    return source[column.of] ?? null;
  }
  const top = source[column.of];
  const base = source[column.per];
  return top === undefined || !base ? null : top / base;
}

/** Agent x day work rows summed per agent; FCR joins on agent_key from its own card. */
function agentNodes(work: Array<Record<string, unknown>>, fcr: Array<Record<string, unknown>>) {
  const fcrByAgent = new Map(fcr.map((r) => [text(r.agent_key), r]));
  const byAgent = new Map<string, AgentNode>();
  work.forEach((r) => {
    const key = text(r.agent_key);
    const node = byAgent.get(key) ?? {
      key,
      name: text(r.agent_name),
      team: text(r.team),
      leader: text(r.team_leader) || 'No leader',
      row: {},
      f: {},
      agents: 1,
      items: 0,
    };
    Object.entries(r).forEach(([k, value]) => {
      const n = num(value);
      if (n !== null && !AGENT_META.includes(k)) {
        node.row[k] = (node.row[k] ?? 0) + n;
      }
    });
    byAgent.set(key, node);
  });
  byAgent.forEach((node) => {
    // contacts handled: each customer contact once, the same for phone, chat and email agents
    node.items = CONTACT_COLUMNS.reduce((n, k) => n + (node.row[k] ?? 0), 0);
    const f = fcrByAgent.get(node.key);
    if (f) {
      ['complete_7d', 'fcr_7d'].forEach((k) => {
        node.f[k] = num(f[k]) ?? 0;
      });
    }
  });
  return byAgent;
}

function AgentsTab({ reader, lagLabel }: { reader: Reader; lagLabel: string }) {
  const [sort, setSort] = useState<{ id: string; dir: 'asc' | 'desc' }>({ id: 'items', dir: 'desc' });
  const [group, setGroup] = useState('All');
  const [leaderFilter, setLeaderFilter] = useState('');
  const [open, setOpen] = useState<Set<string>>(new Set());
  const columns = CATALOGUE.agentsTab.columns.filter((c) => group === 'All' || c.group === group);
  const groupSpans = CATALOGUE.agentsTab.groups
    .map((g) => ({ g, span: columns.filter((c) => c.group === g).length }))
    .filter((x) => x.span > 0);

  const byAgent = agentNodes(reader.rows('agents_work'), reader.rows('agents_fcr'));
  const allAgents = [...byAgent.values()];
  const leaders = [...new Set(allAgents.map((a) => a.leader))].sort((a, b) => a.localeCompare(b));
  const agents = allAgents.filter((a) => !leaderFilter || a.leader === leaderFilter);

  const sortValue = (n: AgentNode): number | string | null => {
    if (sort.id === 'agent') {
      return n.name;
    }
    if (sort.id === 'items') {
      return n.items;
    }
    const column = CATALOGUE.agentsTab.columns.find((c) => c.id === sort.id);
    return column ? agentValue(n, column) : null;
  };
  const compare = (a: AgentNode, b: AgentNode) => {
    const x = sortValue(a);
    const y = sortValue(b);
    if (x === null || x === undefined) {
      return y === null || y === undefined ? a.name.localeCompare(b.name) : 1;
    }
    if (y === null || y === undefined) {
      return -1;
    }
    return (x > y ? 1 : x < y ? -1 : 0) * (sort.dir === 'desc' ? -1 : 1) || a.name.localeCompare(b.name);
  };

  // team (biggest first) > leader subtotal (sorted by the chosen column) > agents (same sort)
  const teams = [...new Set(agents.map((a) => a.team))]
    .map((team) => {
      const leaderNodes = [...new Set(agents.filter((a) => a.team === team).map((a) => a.leader))]
        .map((leader) => {
          const members = agents.filter((a) => a.team === team && a.leader === leader).sort(compare);
          return { node: sumNodes(`${team}|${leader}`, leader, team, leader, members), members };
        })
        .sort((a, b) => compare(a.node, b.node));
      return {
        node: sumNodes(
          team,
          team,
          team,
          '',
          leaderNodes.map((l) => l.node),
        ),
        leaders: leaderNodes,
      };
    })
    .sort((a, b) => b.node.agents - a.node.agents || a.node.name.localeCompare(b.node.name));

  const toggle = (key: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  const leaderKeys = teams.flatMap((t) => t.leaders.map((l) => l.node.key));
  const allOpen = leaderKeys.length > 0 && leaderKeys.every((k) => open.has(k));

  const headerCell = (id: string, label: string, align: 'left' | 'right' = 'right', sticky = false) => (
    <TableCell
      key={id}
      align={align}
      sortDirection={sort.id === id ? sort.dir : false}
      sx={{ whiteSpace: 'nowrap', ...(sticky ? { left: 0, zIndex: 4, minWidth: 240 } : {}) }}
    >
      <Tooltip
        title={
          id === 'fcr' || id === 'fcr_scored'
            ? lagLabel
            : id === 'items'
              ? 'Calls answered + callbacks answered + chats handled + emails handled: each customer contact once.'
              : ''
        }
        arrow
      >
        <TableSortLabel
          active={sort.id === id}
          direction={sort.id === id ? sort.dir : id === 'agent' ? 'asc' : 'desc'}
          onClick={() =>
            setSort({
              id,
              dir: sort.id === id ? (sort.dir === 'desc' ? 'asc' : 'desc') : id === 'agent' ? 'asc' : 'desc',
            })
          }
          sx={{
            '&.Mui-active, &:hover, &:focus': { color: 'inherit' },
            '&.Mui-active .MuiTableSortLabel-icon': { color: 'inherit' },
          }}
        >
          {label}
        </TableSortLabel>
      </Tooltip>
    </TableCell>
  );
  const stickyBody = (bg: string) => ({ position: 'sticky', left: 0, zIndex: 2, bgcolor: bg, minWidth: 240 }) as const;
  const metricCells = (n: AgentNode, weight: number, bg?: string) =>
    columns.map((c) => (
      <TableCell key={c.id} align="right" sx={{ ...NUM, fontWeight: weight, ...(bg ? { bgcolor: bg } : {}) }}>
        {formatValue(agentValue(n, c), c.format)}
      </TableCell>
    ));
  const itemsCell = (n: AgentNode, weight: number, bg?: string) => (
    <TableCell align="right" sx={{ ...NUM, fontWeight: weight, ...(bg ? { bgcolor: bg } : {}) }}>
      {formatValue(n.items)}
    </TableCell>
  );
  const problem = reader.problem('agents_work');

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={group}
          onChange={(_, value) => value && setGroup(value)}
          aria-label="Metric group"
        >
          {['All', ...CATALOGUE.agentsTab.groups].map((g) => (
            <ToggleButton key={g} value={g} sx={{ textTransform: 'none', px: 1.25 }}>
              {g}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="cs-daily-ops-leader">Team leader</InputLabel>
          <Select
            labelId="cs-daily-ops-leader"
            label="Team leader"
            value={leaderFilter}
            onChange={(event) => setLeaderFilter(event.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            {leaders.map((l) => (
              <MenuItem key={l} value={l}>
                {l}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          size="small"
          onClick={() => setOpen(allOpen ? new Set() : new Set(leaderKeys))}
          sx={{ textTransform: 'none' }}
        >
          {allOpen ? 'Collapse all' : 'Expand all'}
        </Button>
      </Stack>
      <Problem text={problem} />
      <TableContainer sx={{ maxHeight: 720 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell colSpan={2} sx={{ py: 0.5, position: 'static' }} />
              {groupSpans.map(({ g, span }) => (
                <TableCell
                  key={g}
                  colSpan={span}
                  align="center"
                  sx={{ py: 0.5, position: 'static', fontWeight: 600, borderLeft: '1px solid', borderColor: 'divider' }}
                >
                  {g}
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              {headerCell('agent', 'Team / leader / agent', 'left', true)}
              {headerCell('items', 'Contacts handled')}
              {columns.map((column) => headerCell(column.id, column.label))}
            </TableRow>
          </TableHead>
          <TableBody>
            {teams.map((team) => (
              <React.Fragment key={team.node.key}>
                <TableRow sx={{ '& td': { borderTop: '2px solid', borderColor: 'divider' } }}>
                  <TableCell sx={{ ...stickyBody('grey.100'), fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {team.node.name}
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      {team.node.agents} agents
                    </Typography>
                  </TableCell>
                  {itemsCell(team.node, 700, 'grey.100')}
                  {metricCells(team.node, 700, 'grey.100')}
                </TableRow>
                {team.leaders.map(({ node, members }) => (
                  <React.Fragment key={node.key}>
                    <TableRow hover onClick={() => toggle(node.key)} sx={{ cursor: 'pointer' }}>
                      <TableCell
                        sx={{ ...stickyBody('background.paper'), fontWeight: 600, whiteSpace: 'nowrap', pl: 1 }}
                      >
                        <Box
                          component="span"
                          aria-label={open.has(node.key) ? 'Collapse' : 'Expand'}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 18,
                            height: 18,
                            mr: 1,
                            border: '1px solid',
                            borderColor: 'text.secondary',
                            borderRadius: 0.5,
                            fontSize: 14,
                            lineHeight: 1,
                          }}
                        >
                          {open.has(node.key) ? '−' : '+'}
                        </Box>
                        {node.name}
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          {node.agents}
                        </Typography>
                      </TableCell>
                      {itemsCell(node, 600)}
                      {metricCells(node, 600)}
                    </TableRow>
                    {open.has(node.key) &&
                      members.map((a) => (
                        <TableRow key={a.key} hover>
                          <TableCell sx={{ ...stickyBody('background.paper'), whiteSpace: 'nowrap', pl: 5 }}>
                            {a.name}
                          </TableCell>
                          {itemsCell(a, 400)}
                          {metricCells(a, 400)}
                        </TableRow>
                      ))}
                  </React.Fragment>
                ))}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {!agents.length && !problem && (
        <Typography variant="caption" color="text.disabled">
          No agent work in this slice.
        </Typography>
      )}
    </Paper>
  );
}

/* ---------- live board: today so far from the hourly Genesys and Zendesk landing ---------- */
type LiveTile = (typeof CATALOGUE.live)[number]['tiles'][number];
const BOARD = {
  bg: '#0d0d0d',
  group: '#1a1a19',
  tile: '#242422',
  ink: '#f0efec',
  muted: '#c3c2b7',
  dim: '#898781',
  line: '#383835',
  good: '#0f3d12',
  warn: '#4d3a06',
  bad: '#4a1414',
} as const;
const LIVE_REFRESH_MS = 10 * 60 * 1000;

// sums the team rows the select keeps; "oldest" columns take the max, stamps and keys are skipped
const liveAgg = (rows: Array<Record<string, unknown>>, keep: (team: string) => boolean) => {
  const out: Record<string, number> = {};
  rows
    .filter((r) => keep(text(r.team)))
    .forEach((r) =>
      Object.entries(r).forEach(([k, v]) => {
        const n = num(v);
        if (k === 'team' || k === 'hour' || n === null) {
          return;
        }
        out[k] = k.startsWith('oldest') ? Math.max(out[k] ?? 0, n) : (out[k] ?? 0) + n;
      }),
    );
  return out;
};
const liveStatus = (tile: LiveTile, v: number | null): 'good' | 'warn' | 'bad' | null => {
  if (v === null) {
    return null;
  }
  if (tile.good !== undefined) {
    return v >= tile.good ? 'good' : v >= (tile.warn ?? tile.good) ? 'warn' : 'bad';
  }
  if (tile.good_max !== undefined) {
    return v <= tile.good_max ? 'good' : v <= (tile.warn_max ?? tile.good_max) ? 'warn' : 'bad';
  }
  return null;
};
const niceMax = (max: number) => {
  const p = 10 ** Math.floor(Math.log10(Math.max(1, max)));
  return [1, 2, 5, 10].map((m) => m * p).find((n) => n >= max) ?? max;
};

function HourBars({
  title,
  rows,
  keys,
  labels,
}: {
  title: string;
  rows: Array<Record<string, number>>;
  keys: [string, string];
  labels: [string, string];
}) {
  const byHour = new Map(rows.map((r) => [r.hour, r]));
  const W = 560;
  const H = 140;
  const pad = { l: 30, r: 8, t: 8, b: 20 };
  const iw = W - pad.l - pad.r;
  const ih = H - pad.t - pad.b;
  const max = niceMax(Math.max(1, ...rows.flatMap((r) => keys.map((k) => r[k] ?? 0))));
  const slot = iw / 24;
  const gap = 2;
  const bw = Math.min(10, (slot - 4) / keys.length - gap);
  const y = (v: number) => pad.t + ih - (v / max) * ih;
  return (
    <Box sx={{ bgcolor: BOARD.group, borderRadius: 3, p: 1.5, pb: 1 }}>
      <Typography sx={{ color: BOARD.muted, fontSize: 13, fontWeight: 600, textTransform: 'uppercase', mb: 1 }}>
        {title}
      </Typography>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={title}>
        <line x1={pad.l} x2={pad.l + iw} y1={pad.t + ih} y2={pad.t + ih} stroke={BOARD.line} />
        <text x={pad.l - 4} y={pad.t + 4} textAnchor="end" fill={BOARD.dim} fontSize={11}>
          {formatValue(max)}
        </text>
        {Array.from({ length: 24 }, (_, h) => {
          const x0 = pad.l + h * slot + (slot - (bw + gap) * keys.length) / 2;
          const r = byHour.get(h);
          return (
            <g key={h}>
              {(h % 6 === 0 || h === 23) && (
                <text x={pad.l + h * slot + slot / 2} y={H - 6} textAnchor="middle" fill={BOARD.dim} fontSize={11}>
                  {String(h).padStart(2, '0')}
                </text>
              )}
              {r &&
                keys.map((k, i) => (
                  <rect
                    key={k}
                    x={x0 + i * (bw + gap)}
                    y={y(r[k] ?? 0)}
                    width={bw}
                    height={Math.max(0, (ih * (r[k] ?? 0)) / max)}
                    fill={SERIES[i]}
                    rx={3}
                  >
                    <title>{`${String(h).padStart(2, '0')}:00 ${labels[i]} ${formatValue(r[k] ?? 0)}`}</title>
                  </rect>
                ))}
            </g>
          );
        })}
      </svg>
      <Stack direction="row" spacing={2}>
        {labels.map((l, i) => (
          <Stack key={l} direction="row" spacing={0.5} alignItems="center">
            <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: SERIES[i] }} />
            <Typography sx={{ color: BOARD.muted, fontSize: 12 }}>{l}</Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

function LiveBoard({
  reader,
  keep,
  refreshedAt,
  loading,
  onRefresh,
}: {
  reader: Reader;
  keep: (team: string) => boolean;
  refreshedAt: number | null;
  loading: boolean;
  onRefresh: () => void;
}) {
  const calls = reader.rows('live_calls');
  const tickets = reader.rows('live_tickets');
  const problem = reader.problem('live_calls') ?? reader.problem('live_tickets');
  const agg = { calls: liveAgg(calls, keep), tickets: liveAgg(tickets, keep) };
  const get = (path: string): number | undefined => {
    const [source, column] = path.split('.');
    const o = source === 'calls' ? agg.calls : agg.tickets;
    return column in o ? o[column] : undefined;
  };
  const loaded = calls.length > 0 || tickets.length > 0;
  const dataTo = (rows: Array<Record<string, unknown>>, key: string) => {
    const stamps = rows
      .map((r) => text(r[key]))
      .filter(Boolean)
      .sort();
    return stamps[stamps.length - 1] ?? '';
  };
  const hourly = (rows: Array<Record<string, unknown>>, keys: Array<string>) => {
    const m = new Map<number, Record<string, number>>();
    rows
      .filter((r) => keep(text(r.team)))
      .forEach((r) => {
        const h = num(r.hour) ?? -1;
        const o = m.get(h) ?? { hour: h };
        keys.forEach((k) => {
          o[k] = (o[k] ?? 0) + (num(r[k]) ?? 0);
        });
        m.set(h, o);
      });
    return [...m.values()];
  };
  const today = new Date().toLocaleDateString('en-AU', {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'Australia/Sydney',
  });
  const stamp = (ms: number | null) =>
    ms
      ? new Date(ms).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', timeZone: 'Australia/Sydney' })
      : '…';
  return (
    <Box sx={{ bgcolor: BOARD.bg, color: BOARD.ink, borderRadius: 4, p: { xs: 2, md: 2.5 } }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ md: 'center' }}
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Typography variant="h6" component="h2" sx={{ color: BOARD.ink }}>
          Customer Service · {today}
        </Typography>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography sx={{ color: BOARD.muted, fontSize: 13 }}>
            Data to {dataTo(calls, 'last_contact') || '…'} (Genesys) · {dataTo(tickets, 'last_update') || '…'} (Zendesk)
            · read {stamp(refreshedAt)}
          </Typography>
          <Button size="small" onClick={onRefresh} disabled={loading} sx={{ color: BOARD.muted }}>
            {loading ? 'Refreshing…' : 'Refresh'}
          </Button>
        </Stack>
      </Stack>
      {problem && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {problem}
        </Alert>
      )}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 1.75 }}>
        {CATALOGUE.live
          .map((group) => ({ ...group, tiles: group.tiles.filter((tile) => tile.src || tile.ratio) }))
          .filter((group) => group.tiles.length > 0)
          .map((group) => (
            <Box key={group.id} sx={{ bgcolor: BOARD.group, borderRadius: 3, p: 1.5 }}>
              <Typography sx={{ color: BOARD.muted, fontSize: 13, fontWeight: 600, textTransform: 'uppercase', mb: 1 }}>
                {group.title}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
                {group.tiles.map((tile) => {
                  let value: number | null | undefined;
                  if (tile.ratio) {
                    const top = get(tile.ratio[0]);
                    const base = get(tile.ratio[1]);
                    value = top === undefined || base === undefined ? undefined : base ? top / base : null;
                  } else if (tile.src) {
                    value = get(tile.src);
                  }
                  const built = Boolean(tile.src || tile.ratio);
                  const na = !built || (loaded && value === undefined);
                  const status = na ? null : liveStatus(tile, value ?? null);
                  const bg = na ? BOARD.tile : status ? BOARD[status] : BOARD.tile;
                  return (
                    <Box
                      key={tile.id}
                      sx={{
                        bgcolor: bg,
                        border: na ? `1px dashed ${BOARD.line}` : undefined,
                        borderRadius: 2.5,
                        p: '10px 12px',
                        minHeight: 84,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography sx={{ color: BOARD.muted, fontSize: 12 }}>{tile.label}</Typography>
                      {na ? (
                        <Box>
                          <Typography sx={{ color: BOARD.dim, fontSize: 13, fontWeight: 500 }}>
                            {!built && tile.feas === 'needs_5min' ? 'Needs a 5-minute feed' : 'Not built yet'}
                          </Typography>
                          {tile.why && <Typography sx={{ color: BOARD.dim, fontSize: 10 }}>{tile.why}</Typography>}
                        </Box>
                      ) : (
                        <Typography sx={{ ...NUM, fontSize: 30, fontWeight: 700, lineHeight: 1.05, color: BOARD.ink }}>
                          {loaded ? formatValue(value ?? null, tile.format) : '…'}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ))}
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.75, mt: 1.75 }}>
        <HourBars
          title="Calls by hour today"
          rows={hourly(reader.rows('live_calls_hourly'), ['calls_offered', 'calls_answered'])}
          keys={['calls_offered', 'calls_answered']}
          labels={['Offered', 'Answered']}
        />
        <HourBars
          title="Tickets by hour today"
          rows={hourly(reader.rows('live_tickets_hourly'), ['tickets_created', 'tickets_solved'])}
          keys={['tickets_created', 'tickets_solved']}
          labels={['Created', 'Solved']}
        />
      </Box>
      <Typography sx={{ color: BOARD.dim, fontSize: 12, mt: 1.5 }}>
        Today so far in Sydney time, from the hourly Genesys and Zendesk landing (20 to 75 minutes behind). Today's
        figures settle into the CS Daily at the 03:00 build; the hourly view is not reconciled until then. Reloads every
        10 minutes while this tab is open.
      </Typography>
    </Box>
  );
}

/* ---------- page ---------- */
function CsDailyOpsContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const filters = useMemo(() => parseFilters(new URLSearchParams(location.search)), [location.search]);
  const update = (change: Partial<Filters>) =>
    navigate({
      pathname: location.pathname,
      search: writeFilters({ ...filters, ...change }, new URLSearchParams(location.search)).toString(),
    });
  const request = useMemo(() => lensRequest(filters), [filters]);
  const query = useGetReportingLeLensData(LENS_ID, request);
  useEffect(() => {
    if (filters.tab !== 'live') {
      return undefined;
    }
    const timer = window.setInterval(() => query.refetch(), LIVE_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [filters.tab, query]);
  const cards = query.data?.cards ?? [];
  const lastOptions = useRef<Record<string, Array<string>>>({});
  if (query.data?.filterOptions && Object.keys(query.data.filterOptions).length) {
    lastOptions.current = query.data.filterOptions;
  }
  const options = lastOptions.current;
  const ready = query.isSuccess && !query.isFetching && !query.isShowingPreviousData;
  const failure = query.error as ApiError | null;
  const anyPeriod = cards.find((card) => card.period && !card.unbound)?.period;
  const periodDays = anyPeriod
    ? Math.round((Date.parse(anyPeriod.current.to) - Date.parse(anyPeriod.current.from)) / 86400000) + 1
    : 1;
  const filtersActive = Boolean(filters.selections.region || filters.selections.department || filters.selections.team);
  const reader: Reader = {
    card: (id) => cards.find((card) => card.cardId === id),
    problem: (id) => {
      const card = cards.find((item) => item.cardId === id);
      if (!card) {
        return query.isFetching ? 'Loading…' : 'This card is still loading.';
      }
      if (card.status === 'ready' || card.status === 'empty') {
        return null;
      }
      return `This card could not load. ${card.message ?? ''}`.trim();
    },
    rows: (id) => {
      const card = cards.find((item) => item.cardId === id);
      return card && (card.status === 'ready' || card.status === 'empty') ? (card.rows ?? []) : [];
    },
    filtersActive,
    periodDays,
    period: anyPeriod ? { from: anyPeriod.current.from, to: anyPeriod.current.to } : null,
  };
  const periodLabel = anyPeriod ? anyPeriod.label.current : '';
  const cmpLabel = anyPeriod?.label.comparison
    ? `vs ${anyPeriod.label.comparison}`
    : filters.comparison === 'year'
      ? 'vs the same dates last year'
      : 'vs the previous period';
  const lagFor = (days: number) =>
    anyPeriod
      ? `FCR windows finalised ${anyPeriod.current.from} to ${anyPeriod.current.to}: contacts from ${shift(anyPeriod.current.from, -days)} to ${shift(anyPeriod.current.to, -days)}.`
      : '';
  // team filters come from the roster-backed teams card (never filtered): department, then dropdown order
  const teamRows = reader
    .rows('teams')
    .map((r) => ({
      team: text(r.team_name),
      department: text(r.department_name),
      region: text(r.region_name),
      order: num(r.sort_order) ?? 999,
    }))
    .sort((a, b) => a.order - b.order);
  // cascading options from the roster: region > department > team; each list keeps every option still possible
  const sel = filters.selections;
  const inRegion = teamRows.filter((r) => !sel.region || r.region === sel.region);
  const regionOptions = [...new Set(teamRows.map((r) => r.region).filter(Boolean))];
  const departmentOptions = [...new Set(inRegion.map((r) => r.department))];
  const teamOptions = inRegion.filter((r) => !sel.department || r.department === sel.department).map((r) => r.team);
  const optionsFor = (key: string) =>
    key === 'team'
      ? teamOptions
      : key === 'department'
        ? departmentOptions
        : key === 'region'
          ? regionOptions
          : (options[key] ?? []);
  // a choice that no longer fits the others is cleared
  const reconcile = (next: Filters['selections']) => {
    const rows = teamRows.filter((r) => !next.region || r.region === next.region);
    const department = next.department && rows.some((r) => r.department === next.department) ? next.department : null;
    const team =
      next.team && rows.some((r) => r.team === next.team && (!department || r.department === department))
        ? next.team
        : null;
    return { ...next, department, team };
  };
  const summary = Object.fromEntries(
    Object.keys(CATALOGUE.itemIndex)
      .filter((id) => !id.endsWith('_team'))
      .map((id) => {
        const f = readItem(id, reader);
        return [f?.item.label ?? id, f?.current ?? null];
      }),
  );
  const pageView = csDailyOpsPageView({
    ready,
    filters,
    options: { ...options, region: regionOptions, department: departmentOptions, team: teamOptions },
    period: periodLabel,
    summary,
  });
  usePageBridge({
    pageId: CS_DAILY_OPS_PAGE_ID,
    view: pageView,
    edit: (changes) => {
      if (!ready) {
        throw new Error('Page data is still loading.');
      }
      update(editFromBridge(changes, pageView, filters));
    },
  });

  const renderCard = (layout: LayoutCard) => {
    switch (layout.type) {
      case 'channels':
        return <ChannelsCard layout={layout} reader={reader} cmpLabel={cmpLabel} />;
      case 'stats':
        return <StatsCard layout={layout} reader={reader} cmpLabel={cmpLabel} />;
      case 'matrix':
        return <MatrixCard layout={layout} reader={reader} cmpLabel={cmpLabel} />;
      case 'stacked':
        return <StackedCard layout={layout} reader={reader} />;
      case 'bar':
        return (
          <BarCard layout={layout} reader={reader} lagLabel={layout.lagDays ? lagFor(layout.lagDays) : undefined} />
        );
      case 'bars':
        return <BarsCard layout={layout} reader={reader} />;
      default:
        return (
          <TableCard layout={layout} reader={reader} lagLabel={layout.lagDays ? lagFor(layout.lagDays) : undefined} />
        );
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="h5">CS Daily Ops</Typography>
            <Typography variant="body2" color="text.secondary">
              {periodLabel ? `${periodLabel} · ${cmpLabel}` : 'Loading…'}
            </Typography>
          </Box>
          <PageChatButton />
        </Stack>
        <Tabs value={filters.tab} onChange={(_, value) => update({ tab: value })}>
          {TABS.map((tab) => (
            <Tab key={tab.value} value={tab.value} label={tab.label} />
          ))}
        </Tabs>
        {
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              // above the tables' sticky header rows and the Agents tab's frozen column
              zIndex: 10,
              bgcolor: 'background.default',
              py: 1,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              {filters.tab !== 'live' && (
                <>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={filters.preset}
                    onChange={(_, value) => value && update({ preset: value })}
                    aria-label="Period"
                  >
                    {PRESETS.map((preset) => (
                      <Tooltip key={preset.value} title={preset.note ?? ''} arrow>
                        <ToggleButton value={preset.value} sx={{ textTransform: 'none', px: 1.25 }}>
                          {preset.label}
                        </ToggleButton>
                      </Tooltip>
                    ))}
                  </ToggleButtonGroup>
                  {filters.tab === 'dashboard' && (
                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      value={filters.comparison}
                      onChange={(_, value) => value && update({ comparison: value })}
                      aria-label="Comparison"
                    >
                      {COMPARISONS.map((comparison) => (
                        <Tooltip key={comparison.value} title={comparison.note} arrow>
                          <ToggleButton value={comparison.value} sx={{ textTransform: 'none', px: 1.25 }}>
                            {comparison.label}
                          </ToggleButton>
                        </Tooltip>
                      ))}
                    </ToggleButtonGroup>
                  )}
                </>
              )}
              {SELECTS.filter(({ key }) => filters.tab !== 'live' || key !== 'region').map(({ key, label }) => (
                <FormControl key={key} size="small" sx={{ minWidth: 150 }}>
                  <InputLabel id={`cs-daily-ops-${key}`}>{label}</InputLabel>
                  <Select
                    labelId={`cs-daily-ops-${key}`}
                    label={label}
                    value={filters.selections[key] ?? ''}
                    onChange={(event) =>
                      update({
                        selections: reconcile({ ...filters.selections, [key]: event.target.value || null }),
                      })
                    }
                  >
                    <MenuItem value="">All</MenuItem>
                    {optionsFor(key).map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ))}
            </Stack>
          </Box>
        }
        {failure && <Alert severity="error">{failure.message ?? 'The dashboard could not load.'}</Alert>}
        {filters.tab === 'dashboard' &&
          SECTIONS.map((section) => (
            <Box key={section.id}>
              <Typography variant="h6" sx={{ mb: 0.5 }}>
                {section.title}
              </Typography>
              <Box
                sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}
              >
                {CATALOGUE.layout
                  .filter((layout) => layout.section === section.id)
                  .map((layout) => (
                    <Box
                      key={layout.id}
                      sx={{
                        gridColumn:
                          layout.size === 'wide' || ['channels', 'matrix', 'stacked', 'bars'].includes(layout.type)
                            ? '1 / -1'
                            : undefined,
                        minWidth: 0,
                      }}
                    >
                      {renderCard(layout)}
                    </Box>
                  ))}
              </Box>
            </Box>
          ))}
        {filters.tab === 'agents' && <AgentsTab reader={reader} lagLabel={lagFor(7)} />}
        {filters.tab === 'live' && (
          <LiveBoard
            reader={reader}
            keep={(team) =>
              filters.selections.team
                ? team === filters.selections.team
                : filters.selections.department
                  ? teamRows.some((r) => r.team === team && r.department === filters.selections.department)
                  : true
            }
            refreshedAt={query.dataUpdatedAt || null}
            loading={query.isFetching}
            onRefresh={() => query.refetch()}
          />
        )}
        <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere', pb: 2 }}>
          Source: {SOURCE} First-contact resolution counts the contacts whose 7-day window finalised in the period.
        </Typography>
      </Stack>
    </Container>
  );
}

export default function CsDailyOpsPage() {
  return (
    <MUIProvider>
      <PageChatLayout>
        <CsDailyOpsContent />
      </PageChatLayout>
    </MUIProvider>
  );
}
